import React, { useContext } from 'react'

import {
  PublicationFragment,
  PublicationItemFragment,
  PublicWorkspaceMinimalFragment,
} from '@sketch/gql-types'

import { Dropdown, ModalContext, downloadFile } from '@sketch/components'

import {
  RenameShareModal,
  routes,
  useLicenseAgreement,
  LaunchSketchAlert,
  useUserSignedIn,
} from '@sketch/modules-common'

import CopyLink from '../../../shares/components/ShareActionsDropdown/CopyLink'
import { UnpublishPublicationModal } from '../../modals'
import { matchPath, useHistory, useLocation } from 'react-router'

interface PublicationActionsDropdownProps {
  publication: PublicationItemFragment | PublicationFragment
  workspace: PublicWorkspaceMinimalFragment
  onDuplicate: () => void
  isDuplicating?: boolean
}

export const PublicationActionsDropdown: React.FC<
  PublicationActionsDropdownProps
> = ({ publication, workspace, onDuplicate, isDuplicating }) => {
  const { showModal } = useContext(ModalContext)
  const isUserSignedIn = useUserSignedIn()
  const history = useHistory()
  const { pathname } = useLocation()

  const { share } = publication

  const { showLicenseAgreementModal } = useLicenseAgreement({
    shareIdentifier: share.identifier,
  })

  const isDownloadAvailable = share.version?.document?.downloadAvailable
  const downloadUrl = share.version?.document?.url
  const isAbleToDuplicate = isUserSignedIn && share.userCanInspect
  const isLibrary = share.shareType === 'LIBRARY'
  const isTemplate = share.shareType === 'TEMPLATE'

  const isPublicationDetailView =
    workspace.profile?.shortUrlName &&
    !!matchPath(pathname, {
      path: routes.WORKSPACE_PROFILE_DOCUMENT.create({
        publicationId: publication.identifier,
        shortUrlName: workspace.profile.shortUrlName,
      }),
    })

  const hasMainActions =
    isAbleToDuplicate || isLibrary || (isDownloadAvailable && downloadUrl)

  const isWorkspaceMember = workspace
    ? workspace.userRole !== null && workspace.userRole !== 'GUEST'
    : false

  const handleViewInWorkspace = () => {
    const shareViewPath = routes.SHARE_VIEW.create({
      shareID: share.identifier,
    })
    history.push(shareViewPath)
  }

  // Ensure the workspace profile is available before rendering actions
  if (!workspace.profile) {
    return null
  }

  return (
    <>
      {isAbleToDuplicate && (
        <Dropdown.Item
          onClick={() =>
            showLicenseAgreementModal({
              helpText:
                'Read and agree to the License Agreement to duplicate this Document.',
              agreeAction: onDuplicate,
            })
          }
          disabled={isDuplicating}
        >
          {isTemplate ? 'Add Template' : 'Duplicate'}
        </Dropdown.Item>
      )}
      {isLibrary && (
        <Dropdown.Item
          onClick={() =>
            showLicenseAgreementModal({
              helpText:
                'Read and agree to the License Agreement to add this Library.',
              agreeAction: () =>
                showModal(LaunchSketchAlert, {
                  shareId: share.identifier,
                  action: 'addLibrary',
                }),
            })
          }
        >
          Add Library
        </Dropdown.Item>
      )}
      {isDownloadAvailable && downloadUrl && (
        <Dropdown.Item
          onClick={() =>
            showLicenseAgreementModal({
              helpText:
                'Read and agree to the License Agreement to download this Document.',
              agreeAction: () => {
                downloadFile(downloadUrl)
              },
            })
          }
        >
          Download
        </Dropdown.Item>
      )}
      {hasMainActions && <Dropdown.Divider />}
      <CopyLink
        publicationIdentifier={publication.identifier}
        shortUrlName={workspace.profile.shortUrlName}
      />
      {isWorkspaceMember && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleViewInWorkspace}>
            View in Workspace
          </Dropdown.Item>
          {isWorkspaceMember && (
            <Dropdown.Item
              onClick={() => {
                showModal(RenameShareModal, {
                  id: share.identifier,
                  name: share.name,
                })
              }}
            >
              Rename
            </Dropdown.Item>
          )}
          {isWorkspaceMember && <Dropdown.Divider />}
          <Dropdown.Item
            intent="negative"
            onClick={() => {
              showModal(UnpublishPublicationModal, {
                shareName: share.name,
                shareIdentifier: share.identifier,
                workspaceIdentifier: workspace.identifier,
                publicCommentsEnabled: share.publicCommentsEnabled,
                publicInspectEnabled: share.publicInspectEnabled,
                publicAccessLevel: share.publicAccessLevel,
                onUnpublish: () => {
                  // If the user is viewing the publication he is removing,
                  // we redirect him back to the workspace profile edit page
                  if (isPublicationDetailView) {
                    history.push(
                      routes.WORKSPACE_PROFILE_EDIT.create({
                        workspaceId: workspace.identifier,
                      })
                    )
                  }
                },
              })
            }}
          >
            Remove from profile…
          </Dropdown.Item>
        </>
      )}
    </>
  )
}
